/**
 * 商务风尚模版 - 单页
 *
 * 设计理念：深墨绿页头条 + 商务正文卡
 * 与 sunset（琥珀渐变头图 + 圆角卡片）完全不同：
 * - 页头：深墨绿渐变 + 金色竖条装饰
 * - 正文：方角白色卡片 + 细边框 + 金色引用链接
 * - 面包屑 + 内容区居中阅读
 */

import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/templates/executive/components/Navbar";
import ExecutiveFooter from "@/templates/executive/components/Footer";
import { getSinglePage } from "@/utils/api";

export default function ExecutiveSinglePage() {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    getSinglePage(slug)
      .then((data) => setPage(data))
      .catch(() => null)
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <LoadingSkeleton />;
  if (!page) return <NotFound />;

  return (
    <div className="min-h-screen bg-[#F5F6F4]">
      <Navbar />

      {/* ── 页面头图区域：深墨绿 + 金色装饰 ── */}
      <section className="relative bg-gradient-to-br from-[#1F4E3D] via-[#1A4334] to-[#12382A] py-14 md:py-16 overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#C2A04B] via-[#DDC583] to-[#C2A04B]" />
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "repeating-linear-gradient(45deg, #fff 0, #fff 1px, transparent 1px, transparent 14px)" }} />
        <div className="absolute -top-20 right-20 w-64 h-64 rounded-full bg-[#C2A04B]/10 blur-3xl" />
        <div className="relative max-w-4xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-4">
            <span className="w-1.5 h-10 bg-[#C2A04B] rounded-full shrink-0" />
            <h1 className="text-2xl md:text-3xl font-black text-white">
              {page.title}
            </h1>
          </div>
          {/* 装饰横线 */}
          <div className="mt-4 ml-6 flex items-center gap-2">
            <span className="w-10 h-0.5 bg-[#C2A04B]/60 rounded-full" />
            <span className="w-2 h-2 bg-[#DDC583] rounded-full" />
            <span className="w-10 h-0.5 bg-[#C2A04B]/60 rounded-full" />
          </div>
        </div>
      </section>

      {/* ── 面包屑 ── */}
      <div className="bg-white border-b border-[#E2E8E0]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-3 flex items-center gap-1.5 text-sm">
          <Link to="/" className="text-gray-400 hover:text-[#C2A04B] transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line text-xs text-gray-300" />
          <span className="text-[#1F4E3D] font-semibold">{page.title}</span>
        </div>
      </div>

      {/* ── 内容区 ── */}
      <article className="py-10 md:py-14">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="bg-white rounded-md p-6 md:p-10 border border-[#E2E8E0] shadow-sm">
            {page.content ? (
              <div
                className="prose prose-lg max-w-none prose-headings:text-[#1F4E3D] prose-p:text-gray-600 prose-p:leading-loose prose-a:text-[#8C6E2E] prose-a:no-underline hover:prose-a:text-[#C2A04B] prose-strong:text-[#1F4E3D] prose-img:rounded-md prose-img:border prose-img:border-[#E2E8E0]"
                dangerouslySetInnerHTML={{ __html: page.content }}
              />
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-sm bg-[#1F4E3D]/5 border border-[#1F4E3D]/10">
                  <i className="ri-file-text-line text-2xl text-[#C2A04B]" />
                </div>
                <p className="text-gray-400">暂无内容</p>
              </div>
            )}
          </div>

          {/* 返回首页按钮 */}
          <div className="mt-8 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-[#C2A04B] transition-colors group"
            >
              <i className="ri-arrow-left-line group-hover:-translate-x-1 transition-transform" />
              返回首页
            </Link>
          </div>
        </div>
      </article>

      <ExecutiveFooter />
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-[#F5F6F4]">
      <Navbar />
      <section className="bg-gradient-to-br from-[#1F4E3D] to-[#12382A] py-16">
        <div className="max-w-4xl mx-auto px-6 text-center animate-pulse">
          <div className="w-48 h-8 bg-white/20 rounded-md mx-auto" />
          <div className="flex items-center justify-center gap-2 mt-4">
            <div className="w-8 h-0.5 bg-white/20 rounded-full" />
            <div className="w-2 h-2 bg-white/20 rounded-full" />
            <div className="w-8 h-0.5 bg-white/20 rounded-full" />
          </div>
        </div>
      </section>
      <div className="py-12 max-w-3xl mx-auto px-6">
        <div className="animate-pulse space-y-4 p-10 bg-white rounded-md border border-[#E2E8E0]">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className={`bg-gray-200 rounded ${i === 0 ? "h-7 w-2/3" : i === 5 ? "h-4 w-1/3" : "h-4 w-full"}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function NotFound() {
  return (
    <div className="min-h-screen bg-[#F5F6F4] flex items-center justify-center">
      <div className="text-center px-4">
        <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-sm bg-[#1F4E3D]/5 border border-[#1F4E3D]/10">
          <i className="ri-file-damage-line text-3xl text-[#C2A04B]" />
        </div>
        <h1 className="text-xl font-bold text-[#1F4E3D] mb-2">页面未找到</h1>
        <p className="text-sm text-gray-500 mb-6">您访问的单页不存在或已被删除</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#C2A04B] to-[#A8863A] text-[#1F4E3D] text-sm font-bold rounded-sm hover:from-[#D8B86A] hover:to-[#B8912F] transition-all"
        >
          <i className="ri-home-4-line" />
          返回首页
        </Link>
      </div>
    </div>
  );
}
